import React, { useRef } from "react";
import type { FC, ReactElement } from "react";
import {
  useTranslation,
  Trans,
  useBrand,
  createFeature,
} from "@edreamsodigeo/frontend-core";
import {
  Box,
  Body,
  Pill,
  RawMoney,
  Flex,
} from "@edreamsodigeo/prisma-design-system";

import { useIsMeta } from "~/clientState/providers/SessionScopeProvider/SessionScopeProvider";
import { isPrimeDay } from "~/tools/utils";
import type { Money as MoneyType } from "~/generated/graphql";
import { PRIME_DAYS_BANNER_FLIGHT_SUMMARY_MOBILE } from "~/clientState/ab/teams/titans";

interface PromotionBadgeProps {
  nonDiscountedPrice: MoneyType;
  discountedPrice: MoneyType;
  isPrimeDaysEnabled?: boolean;
}

const discountPillStyles = {
  ED: {
    background: "linear-gradient(90deg, #D83945 14.13%, #F9700C 85.87%)",
    color: "neutral.0",
  },
  OP: {
    backgroundColor: "deals.30",
    color: "neutral.0",
  },
  GO: {
    backgroundColor: "info.70",
    color: "neutral.0",
  },
  TR: {
    backgroundColor: "secondary.50",
    color: "primary.90",
  },
};

const Discount: FC<{ amountDiscounted: MoneyType }> = ({
  amountDiscounted,
}): ReactElement => {
  return (
    <strong>
      <RawMoney currency={amountDiscounted?.currency} rounded>
        {amountDiscounted?.amount}
      </RawMoney>
    </strong>
  );
};

const DiscountPill: FC<{ amountDiscounted: MoneyType }> = ({
  amountDiscounted,
}): ReactElement => {
  const brand = useBrand();

  return (
    <Pill
      sx={discountPillStyles[brand]}
      type={"neutral"}
      theme={"soft"}
      rounded
    >
      <Flex flexDirection={"row"}>
        <Body
          type={"xSmall"}
          weight={"normal"}
          color={discountPillStyles[brand].color}
        >
          <Trans
            i18nKey={"equalProminence.primePrice.pillText"}
            components={{
              discount: <Discount amountDiscounted={amountDiscounted} />,
            }}
          />
        </Body>
      </Flex>
    </Pill>
  );
};

const PromotionBadgeComponent: FC<PromotionBadgeProps> = ({
  nonDiscountedPrice,
  discountedPrice,
  isPrimeDaysEnabled = false,
}): ReactElement => {
  const { t } = useTranslation();
  const isMeta = useIsMeta();
  const todayRef = useRef(new Date());
  const today = todayRef.current;
  let isPrimeDays = false;
  const brand = useBrand();
  const PillProperties = {
    ED: "neutral.100",
    GO: "neutral.0",
    OP: "neutral.0",
    TR: "primary.90",
  };

  if (isMeta && isPrimeDaysEnabled) {
    isPrimeDays = isPrimeDay({
      today,
      startDate: t("equalProminence.promobanner.startDate"),
      endDate: t("equalProminence.promobanner.endDate"),
    });
  }

  if (isPrimeDays) {
    return (
      <Box>
        <Pill type={"promotional"} rounded data-testid={"prime-days-pill"}>
          <Body color={PillProperties[brand]} type={"xSmall"}>
            {t("equalProminence.promobanner.pill")}
          </Body>
        </Pill>
      </Box>
    );
  }

  return (
    <DiscountPill
      amountDiscounted={{
        currency: nonDiscountedPrice.currency,
        amount:
          Math.ceil(nonDiscountedPrice.amount) -
          Math.ceil(discountedPrice.amount),
      }}
    />
  );
};

const PrimeDaysPromotionBadgeComponent = (
  args: PromotionBadgeProps,
): ReactElement => (
  <PromotionBadgeComponent {...args} isPrimeDaysEnabled={true} />
);

const PromotionBadge = createFeature({
  name: PRIME_DAYS_BANNER_FLIGHT_SUMMARY_MOBILE,
  variations: [
    PromotionBadgeComponent,
    PrimeDaysPromotionBadgeComponent,
    PrimeDaysPromotionBadgeComponent,
  ],
  version: 1,
});

export default PromotionBadge;
